import { SYSTEM_ROLES } from '../../constants/system';
import translations from '../../translations';
import type { UserAPI } from '../../types/user';

export const AUDIT_TIME_PERIODS = {
    day: {
        label: 'Day',
        value: 'day',
    },
    month: {
        label: 'Last Month',
        value: 'month',
    },
    threeMonths: {
        label: 'Last 3 Months',
        value: '3months',
    },
    sixMonths: {
        label: 'Last 6 Months',
        value: '6months',
    },
    twelveMonths: {
        label: 'Last 12 Months',
        value: '12months',
    },
} as const;

export const DATE_FNS_FORMAT_NO_ZONE = "yyyy-MM-dd'T'HH:mm:ss.SSS";

// The Metrics API returns this hardcoded Public user
export const PUBLIC_USER: UserAPI = {
    id: '52df1a90-3826-4508-b7c2-cde8aa5b72cf',
    email: '',
    firstName: 'Public',
    lastName: 'user',
    createdAt: null,
    isSso: false,
    organizations: [],
    role: SYSTEM_ROLES.standard_user,
    tenants: [],
    tokens: [],
};

export const EVENT_TYPES = {
    'environment.created': {
        name: 'Environment Created',
        description: 'An environment was created',
        logTemplate: translations.AUDIT_event_environment_created,
    },
    'environment.deleted': {
        name: 'Environment Deleted',
        description: 'An environment was deleted',
        logTemplate: translations.AUDIT_event_environment_deleted,
    },
    'environment.variable.created': {
        name: 'Environment Variable Created',
        description: 'An environment variable was created',
        logTemplate: translations.AUDIT_event_environment_variable_added,
    },
    'environment.variable.updated': {
        name: 'Environment Variable Updated',
        description: 'An environment variable was updated',
        logTemplate: translations.AUDIT_event_environment_variable_updated,
    },
    'environment.variable.deleted': {
        name: 'Environment Variable Deleted',
        description: 'An environment variable was deleted',
        logTemplate: translations.AUDIT_event_environment_variable_removed,
    },
    'flow.activated': {
        name: 'Flow activated',
        description: 'A flow version was set as the active one',
        logTemplate: translations.AUDIT_event_flow_activated,
    },
    'flow.deactivated': {
        name: 'Flow deactivated',
        description: 'A flow version was deactivated',
        logTemplate: translations.AUDIT_event_flow_deactivated,
    },
    'flow.added.release': {
        name: 'Flow added to release',
        description: 'A flow was added to a release',
        logTemplate: translations.AUDIT_event_flow_added_to_release,
    },
    'flow.loaded': {
        name: 'Flow loaded',
        description: 'A flow was loaded',
        logTemplate: translations.AUDIT_event_flow_loaded,
    },
    'flow.package.exported': {
        name: 'Flow package exported',
        description: 'A flow package was exported',
        logTemplate: translations.AUDIT_event_flow_package_exported,
    },
    'flow.package.imported': {
        name: 'Flow package imported',
        description: 'A flow package was imported',
        logTemplate: translations.AUDIT_event_flow_package_imported,
    },
    'flow.package.shared': {
        name: 'Flow package shared',
        description: 'A flow package was shared using a sharing token',
        logTemplate: translations.AUDIT_event_flow_package_shared,
    },
    'flow.removed.release': {
        name: 'Flow removed from release',
        description: 'A flow was removed from a release',
        logTemplate: translations.AUDIT_event_flow_removed_from_release,
    },
    'flow.saved': {
        name: 'Flow saved',
        description: 'A flow was saved',
        logTemplate: translations.AUDIT_event_flow_saved,
    },
    'identity.provider.added': {
        name: 'Identity Provider Added',
        description: 'An Identity Provider was added',
        logTemplate: translations.AUDIT_event_identity_provider_added,
    },
    'identity.provider.edited': {
        name: 'Identity Provider Edited',
        description: 'An Identity Provider was edited',
        logTemplate: translations.AUDIT_event_identity_provider_edited,
    },
    'identity.provider.removed': {
        name: 'Identity Provider Removed',
        description: 'An Identity Provider was removed',
        logTemplate: translations.AUDIT_event_identity_provider_removed,
    },
    'release.created': {
        name: 'Release Created',
        description: 'A release was created',
        logTemplate: translations.AUDIT_event_release_created,
    },
    'release.deleted': {
        name: 'Release Deleted',
        description: 'A release was deleted',
        logTemplate: translations.AUDIT_event_release_deleted,
    },
    'release.deployed': {
        name: 'Release Deployed',
        description: 'A release was deployed',
        logTemplate: translations.AUDIT_event_release_deployed,
    },
    'release.rolledback': {
        name: 'Release Rolled Back',
        description: 'Release rolled back',
        logTemplate: translations.AUDIT_event_release_rolled_back,
    },
    'tenant.clearing.applied': {
        name: 'Tenant clearing applied',
        description: 'Deletion of all data in a tenant was performed',
        logTemplate: translations.AUDIT_event_tenant_clearing_applied,
    },
    'tenant.clearing.requested': {
        name: 'Tenant clearing requested',
        description: 'Deletion of all data in a tenant was requested',
        logTemplate: translations.AUDIT_event_tenant_clearing_request,
    },
    'tenant.created': {
        name: 'Tenant created',
        description: 'A new tenant was created',
        logTemplate: translations.AUDIT_event_tenant_created,
    },
    'tenant.deletion.applied': {
        name: 'Tenant deletion applied',
        description: 'A tenant was deleted',
        logTemplate: translations.AUDIT_event_tenant_deletion_applied,
    },
    'tenant.deletion.requested': {
        name: 'Tenant deletion requested',
        description: 'A request was made to delete a tenant',
        logTemplate: translations.AUDIT_event_tenant_deletion_requested,
    },
    'tenant.exported': {
        name: 'Tenant exported',
        description: 'A tenant package was created and exported',
        logTemplate: translations.AUDIT_event_tenant_exported,
    },
    'tenant.imported': {
        name: 'Tenant imported',
        description: 'A tenant package containing flows was imported into this tenant',
        logTemplate: translations.AUDIT_event_tenant_imported,
    },
    'tenant.import.started': {
        name: 'Tenant import started',
        description: 'The process of importing a tenant with flows has begun',
        logTemplate: translations.AUDIT_event_tenant_import_started,
    },
    'tenant.saved': {
        name: 'Tenant saved',
        description: 'A tenant was saved',
        logTemplate: translations.AUDIT_event_tenant_saved,
    },
    'tenant.user.added': {
        name: 'Tenant user added',
        description: 'A user was added to a tenant',
        logTemplate: translations.AUDIT_event_user_added,
    },
    'tenant.user.role.updated': {
        name: 'Tenant user role updated',
        description: 'User role was updated and saved',
        logTemplate: translations.AUDIT_event_user_updated_role,
    },
    'tenant.user.removed': {
        name: 'Tenant user removed',
        description: 'A user was removed from a tenant',
        logTemplate: translations.AUDIT_event_user_removed,
    },
    'tenant.key.added': {
        name: 'Tenant key added',
        description: 'An API key was created for a tenant',
        logTemplate: translations.AUDIT_event_tenant_key_added,
    },
    'tenant.key.removed': {
        name: 'Tenant key removed',
        description: 'An API key was removed for a tenant',
        logTemplate: translations.AUDIT_event_tenant_key_removed,
    },
    'theme.deleted': {
        name: 'Theme Deleted',
        description: 'The user deleted a theme',
        logTemplate: translations.AUDIT_event_theme_deleted,
    },
    'theme.release.added': {
        name: 'Theme Added To Release',
        description: 'The user added a theme to a release',
        logTemplate: translations.AUDIT_event_theme_added_to_release,
    },
    'theme.release.removed': {
        name: 'Theme Removed From Release',
        description: 'The user removed a theme from a release',
        logTemplate: translations.AUDIT_event_theme_removed_from_release,
    },
    'theme.saved': {
        name: 'Theme Saved',
        description: 'The user saved a theme',
        logTemplate: translations.AUDIT_event_theme_saved,
    },
    'user.accepted.public.flow': {
        name: 'User Accepted Public Flow Disclaimer',
        description: 'The user accepted the risk to make a flow publicly accessible',
        logTemplate: translations.AUDIT_user_accepted_public_flow,
    },
    'user.accepted.public.snapshot': {
        name: 'User Accepted Public Snapshot Disclaimer',
        description: 'The user accepted the risk to make a snapshot publicly accessible',
        logTemplate: translations.AUDIT_user_accepted_public_snapshot,
    },
    'user.logged.in': {
        name: 'User Logged In',
        description: 'The user logged into a tenant',
        logTemplate: translations.AUDIT_event_user_logged_in,
    },
    'tenant.public.confirmation.changed': {
        name: 'Tenant Public Flow Confirmation changed',
        description: 'The Public Flow Confirmation tenant setting has been changed',
        logTemplate: translations.AUDIT_event_public_confirmation_tenant_setting,
    },
} as const;
